import axios from '../utils/axiosConfig';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

export interface RewardCategory {
  id: number;
  name: string;
  description: string;
  color_code: string;
  icon: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface RewardSubcategory {
  id: number;
  category_id: number;
  name: string;
  description: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface RewardRecognition {
  id: number;
  category_id: number;
  subcategory_id: number;
  employee_id: string;
  employee_name: string;
  employee_email: string;
  employee_photo?: string;
  award_date: string;
  description: string;
  achievement_details: string;
  is_featured: boolean;
  is_visible: boolean;
  created_at: string;
  updated_at: string;
  category_name?: string;
  subcategory_name?: string;
}

export interface RewardRecognitionWithDetails extends RewardRecognition {
  category: RewardCategory;
  subcategory: RewardSubcategory;
}

export interface CreateRewardRecognitionData {
  category_id: number;
  subcategory_id: number;
  employee_id: string;
  employee_name: string;
  employee_email: string;
  employee_photo?: string;
  award_date: string;
  description: string;
  achievement_details: string;
  is_featured?: boolean;
}

export interface UpdateRewardRecognitionData extends Partial<CreateRewardRecognitionData> {
  id: number;
}

class RewardRecognitionService {
  // Categories
  async getCategories(): Promise<RewardCategory[]> {
    try {
      const response = await axios.get<ApiResponse<RewardCategory[]>>(`${API_BASE_URL}/reward-categories`);
      return (response.data as any)?.success ? (response.data as any)?.data : [];
    } catch (error) {
      console.error('Error fetching reward categories:', error);
      return this.getMockCategories();
    }
  }

  async createCategory(data: Omit<RewardCategory, 'id' | 'created_at' | 'updated_at'>): Promise<RewardCategory | null> {
    try {
      const response = await axios.post(`${API_BASE_URL}/reward-categories`, data);
      return (response.data as any)?.success ? (response.data as any)?.data : null;
    } catch (error) {
      console.error('Error creating reward category:', error);
      return null;
    }
  }

  async updateCategory(id: number, data: Partial<RewardCategory>): Promise<RewardCategory | null> {
    try {
      const response = await axios.put(`${API_BASE_URL}/reward-categories/${id}`, data);
      return (response.data as any)?.success ? (response.data as any)?.data : null;
    } catch (error) {
      console.error('Error updating reward category:', error);
      return null;
    }
  }

  async deleteCategory(id: number): Promise<boolean> {
    try {
      const response = await axios.delete(`${API_BASE_URL}/reward-categories/${id}`);
      return (response.data as any)?.success;
    } catch (error) {
      console.error('Error deleting reward category:', error);
      return false;
    }
  }

  // Subcategories
  async getSubcategories(categoryId?: number): Promise<RewardSubcategory[]> {
    try {
      const url = categoryId 
        ? `${API_BASE_URL}/reward-subcategories?category_id=${categoryId}`
        : `${API_BASE_URL}/reward-subcategories`;
      const response = await axios.get(url);
      return (response.data as any)?.success ? (response.data as any)?.data : [];
    } catch (error) {
      console.error('Error fetching reward subcategories:', error);
      return this.getMockSubcategories(categoryId);
    }
  }

  async createSubcategory(data: Omit<RewardSubcategory, 'id' | 'created_at' | 'updated_at'>): Promise<RewardSubcategory | null> {
    try {
      const response = await axios.post(`${API_BASE_URL}/reward-subcategories`, data);
      return (response.data as any)?.success ? (response.data as any)?.data : null;
    } catch (error) {
      console.error('Error creating reward subcategory:', error);
      return null;
    }
  }

  async updateSubcategory(id: number, data: Partial<RewardSubcategory>): Promise<RewardSubcategory | null> {
    try {
      const response = await axios.put(`${API_BASE_URL}/reward-subcategories/${id}`, data);
      return (response.data as any)?.success ? (response.data as any)?.data : null;
    } catch (error) {
      console.error('Error updating reward subcategory:', error);
      return null;
    }
  }

  async deleteSubcategory(id: number): Promise<boolean> {
    try {
      const response = await axios.delete(`${API_BASE_URL}/reward-subcategories/${id}`);
      return (response.data as any)?.success;
    } catch (error) {
      console.error('Error deleting reward subcategory:', error);
      return false;
    }
  }

  // Reward Recognitions
  async getRewardRecognitions(): Promise<RewardRecognitionWithDetails[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/reward-recognitions`);
      return (response.data as any)?.success ? (response.data as any)?.data : [];
    } catch (error) {
      console.error('Error fetching reward recognitions:', error);
      return this.getMockRewardRecognitions();
    }
  }

  async getRewardRecognitionsByCategory(categoryId: number): Promise<RewardRecognitionWithDetails[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/reward-recognitions?category_id=${categoryId}`);
      return (response.data as any)?.success ? (response.data as any)?.data : [];
    } catch (error) {
      console.error('Error fetching reward recognitions by category:', error);
      return this.getMockRewardRecognitions().filter(r => r.category_id === categoryId);
    }
  }

  async createRewardRecognition(formData: FormData): Promise<RewardRecognition | null> {
    try {
      const response = await axios.post(`${API_BASE_URL}/reward-recognitions`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return (response.data as any)?.success ? (response.data as any)?.data : null;
    } catch (error) {
      console.error('Error creating reward recognition:', error);
      return null;
    }
  }

  async updateRewardRecognition(id: number, formData: FormData): Promise<RewardRecognition | null> {
    try {
      const response = await axios.put(`${API_BASE_URL}/reward-recognitions/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return (response.data as any)?.success ? (response.data as any)?.data : null;
    } catch (error) {
      console.error('Error updating reward recognition:', error);
      return null;
    }
  }

  async deleteRewardRecognition(id: number): Promise<boolean> {
    try {
      const response = await axios.delete(`/reward-recognitions/${id}`);
      return (response.data as any)?.success;
    } catch (error) {
      console.error('Error deleting reward recognition:', error);
      return false;
    }
  }

  // Upload employee photo
  async uploadEmployeePhoto(file: File): Promise<string | undefined> {
    try {
      const formData = new FormData();
      formData.append('photo', file);
      
      const response = await axios.post(`${API_BASE_URL}/reward-recognitions/upload-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return (response.data as any)?.success ? (response.data as any)?.data?.photoUrl : undefined;
    } catch (error) {
      console.error('Error uploading employee photo:', error);
      return undefined;
    }
  }

  // Get photo URL
  getPhotoUrl(photoPath: string | undefined | null): string {
    if (!photoPath || typeof photoPath !== 'string') {
      return '/assets/images/profile-avatar.jpg';
    }
    
    // If it's already a full URL, return as is
    if (photoPath.startsWith('http')) {
      return photoPath;
    }
    
    // If it's a relative path starting with /uploads or /assets, return as is (proxy will handle it)
    if (photoPath.startsWith('/uploads') || photoPath.startsWith('/assets')) {
      return photoPath;
    }
    
    // Otherwise assume it's just a filename, prepend /uploads/
    return `/uploads/${photoPath}`;
  }

  // Mock data methods
  private getMockCategories(): RewardCategory[] {
    return [
      {
        id: 1,
        name: 'Reliability',
        description: 'Recognition for consistent performance and dependability',
        color_code: '#8B4513',
        icon: 'icon-reliability',
        is_active: true,
        sort_order: 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 2,
        name: 'Excellence',
        description: 'Recognition for outstanding quality and exceptional work',
        color_code: '#1E90FF',
        icon: 'icon-excellence',
        is_active: true,
        sort_order: 2,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 3,
        name: 'Agility',
        description: 'Recognition for adaptability and quick response to change',
        color_code: '#D2691E',
        icon: 'icon-agility',
        is_active: true,
        sort_order: 3,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 4,
        name: 'Perseverance',
        description: 'Recognition for persistence and determination in achieving goals',
        color_code: '#2F4F4F',
        icon: 'icon-perseverance',
        is_active: true,
        sort_order: 4,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    ];
  }

  private getMockSubcategories(categoryId?: number): RewardSubcategory[] {
    const allSubcategories = [
      // Reliability subcategories
      { id: 1, category_id: 1, name: 'Consistent Performer', description: 'For employees who consistently deliver quality work', is_active: true, sort_order: 1, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 2, category_id: 1, name: 'Team Player', description: 'For employees who are reliable team members', is_active: true, sort_order: 2, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 3, category_id: 1, name: 'Process Champion', description: 'For employees who follow and improve processes', is_active: true, sort_order: 3, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      
      // Excellence subcategories
      { id: 4, category_id: 2, name: 'Innovation Award', description: 'For innovative solutions and creative thinking', is_active: true, sort_order: 1, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 5, category_id: 2, name: 'Quality Champion', description: 'For maintaining highest quality standards', is_active: true, sort_order: 2, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 6, category_id: 2, name: 'Customer Delight', description: 'For exceptional customer service', is_active: true, sort_order: 3, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      
      // Agility subcategories
      { id: 7, category_id: 3, name: 'Quick Learner', description: 'For rapidly acquiring new skills', is_active: true, sort_order: 1, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 8, category_id: 3, name: 'Change Agent', description: 'For driving positive change in the organization', is_active: true, sort_order: 2, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 9, category_id: 3, name: 'Problem Solver', description: 'For quick and effective problem resolution', is_active: true, sort_order: 3, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      
      // Perseverance subcategories
      { id: 10, category_id: 4, name: 'Goal Achiever', description: 'For consistently meeting and exceeding goals', is_active: true, sort_order: 1, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 11, category_id: 4, name: 'Resilience Award', description: 'For overcoming challenges and setbacks', is_active: true, sort_order: 2, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 12, category_id: 4, name: 'Long Service', description: 'For dedicated long-term service', is_active: true, sort_order: 3, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }
    ];

    return categoryId ? allSubcategories.filter(sub => sub.category_id === categoryId) : allSubcategories;
  }

  private getMockRewardRecognitions(): RewardRecognitionWithDetails[] {
    const categories = this.getMockCategories();
    const subcategories = this.getMockSubcategories();

    return [
      {
        id: 1,
        category_id: 1,
        subcategory_id: 1,
        employee_id: 'EMP001',
        employee_name: 'John Doe',
        employee_email: 'john.doe@jwil.com',
        employee_photo: '',
        award_date: '2024-10-01',
        description: 'Consistent high-quality deliverables',
        achievement_details: 'Delivered 15 projects on time with zero defects',
        is_featured: true,
        is_visible: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        category: categories[0],
        subcategory: subcategories[0]
      },
      {
        id: 2,
        category_id: 2,
        subcategory_id: 4,
        employee_id: 'EMP002',
        employee_name: 'Jane Smith',
        employee_email: 'jane.smith@jwil.com',
        employee_photo: '',
        award_date: '2024-10-15',
        description: 'Innovative solution for client problem',
        achievement_details: 'Developed automated solution saving 40% processing time',
        is_featured: false,
        is_visible: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        category: categories[1],
        subcategory: subcategories[3]
      },
      {
        id: 3,
        category_id: 3,
        subcategory_id: 8,
        employee_id: 'EMP003',
        employee_name: 'Mike Johnson',
        employee_email: 'mike.johnson@jwil.com',
        employee_photo: '',
        award_date: '2024-10-20',
        description: 'Led digital transformation initiative',
        achievement_details: 'Successfully migrated legacy systems to cloud platform',
        is_featured: true,
        is_visible: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        category: categories[2],
        subcategory: subcategories[7]
      },
      {
        id: 4,
        category_id: 4,
        subcategory_id: 10,
        employee_id: 'EMP004',
        employee_name: 'Sarah Wilson',
        employee_email: 'sarah.wilson@jwil.com',
        employee_photo: '',
        award_date: '2024-10-25',
        description: 'Exceeded quarterly targets by 150%',
        achievement_details: 'Generated highest revenue in team history',
        is_featured: false,
        is_visible: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        category: categories[3],
        subcategory: subcategories[9]
      }
    ];
  }
}

export default new RewardRecognitionService();
